import React, { useEffect, useState } from 'react'
import { Box, Typography, Chip, alpha, Skeleton } from '@mui/material'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import urls from 'utils/urls'
import { makeGetRequest } from 'utils/api'
import { convertToUserDateFormate } from 'utils/helpers'
import { getStatutMeta } from 'containers/MesToDo/statusPalette'

// LAP — colonne droite de l'accueil : « tâches arrivant à échéance »
// (demande Alexis, style SAP Ariba : contenu complémentaire à droite du
// feed). Réutilise le queryset déjà exposé par MesToDo.views.TaskOperation
// (?task=cat9 = en retard + dues aujourd'hui, ?task=cat6 = dues sous 7
// jours) plutôt que d'inventer un nouvel endpoint — cf. /todos?filter=…
// (feedback_smartfeed_todo_filters). On fusionne + trie côté client.

const PANEL_LIMIT = 6

function toDateOnly(d) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

export default function UpcomingDeadlinesPanel() {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  const siteUser = useSelector((store) => store.user)
  const [tasks, setTasks] = useState(null) // null = loading
  const [error, setError] = useState(false)

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const [overdueOrToday, dueSoon] = await Promise.all([
          makeGetRequest(urls.taskOperations + '?task=cat9&limit=20', {}, true),
          makeGetRequest(urls.taskOperations + '?task=cat6&limit=20', {}, true),
        ])
        if (!active) return
        const byId = new Map()
        ;[overdueOrToday, dueSoon].forEach((response) => {
          const results = response?.data?.results || []
          results.forEach((task) => byId.set(task.id, task))
        })
        const merged = Array.from(byId.values()).sort(
          (a, b) => new Date(a.DateLimite) - new Date(b.DateLimite),
        )
        setTasks(merged.slice(0, PANEL_LIMIT))
      } catch (e) {
        if (active) setError(true)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [])

  const handleTaskClick = (task) => {
    const today = toDateOnly(new Date())
    const due = toDateOnly(task.DateLimite)
    let filter = 'echeance-proche'
    if (task.Statut === 'En-retard' || due < today) filter = 'overdue'
    else if (due.getTime() === today.getTime()) filter = 'today'
    navigate(`/todos?filter=${filter}&focus=${task.id}`)
  }

  return (
    <Box
      sx={{
        bgcolor: '#1a1d2e',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <ScheduleRoundedIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
        <Typography sx={{ color: '#E0E0E0', fontWeight: 700, fontSize: '0.95rem' }}>
          {t('dashboard.upcomingDeadlines', { defaultValue: 'Échéances à venir' })}
        </Typography>
      </Box>

      {tasks === null && !error && (
        <Box display="flex" flexDirection="column" gap={1}>
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={52}
              sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '10px' }}
            />
          ))}
        </Box>
      )}

      {error && (
        <Typography sx={{ color: alpha('#E0E0E0', 0.6), fontSize: '0.85rem' }}>
          {t('dashboard.upcomingDeadlinesError', {
            defaultValue: 'Impossible de charger vos échéances pour le moment.',
          })}
        </Typography>
      )}

      {tasks && tasks.length === 0 && (
        <Typography sx={{ color: alpha('#E0E0E0', 0.6), fontSize: '0.85rem' }}>
          {t('dashboard.upcomingDeadlinesEmpty', {
            defaultValue: 'Aucune échéance en retard ou sous 7 jours. 🎉',
          })}
        </Typography>
      )}

      {tasks &&
        tasks.map((task) => {
          const meta = getStatutMeta(task.Statut, t)
          return (
            <Box
              key={task.id}
              onClick={() => handleTaskClick(task)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.25,
                borderRadius: '10px',
                bgcolor: 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.07)' },
              }}
            >
              <Box flex={1} minWidth={0}>
                <Typography
                  noWrap
                  sx={{ color: '#E0E0E0', fontSize: '0.85rem', fontWeight: 600 }}
                >
                  {task.Nom}
                </Typography>
                <Box display="flex" alignItems="center" gap={0.75} mt={0.4}>
                  <Chip
                    label={meta.text}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      bgcolor: alpha(meta.color, 0.18),
                      color: meta.color,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                  <Typography sx={{ color: alpha('#E0E0E0', 0.5), fontSize: '0.72rem' }}>
                    {convertToUserDateFormate(siteUser?.user, task.DateLimite)}
                  </Typography>
                </Box>
              </Box>
              <ArrowForwardIosRoundedIcon sx={{ fontSize: 12, color: alpha('#E0E0E0', 0.35) }} />
            </Box>
          )
        })}
    </Box>
  )
}
